'use client'

import { Fragment, forwardRef, useContext } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { serializeAdvancedSearchData } from 'src/_helpers/advancedSearchParams'
import { GlobalContext } from 'src/components/layout/GlobalContext'
import Button from 'src/components/design-system/Button'
import Lightbox from 'src/components/Lightbox'
import Typography from 'src/components/design-system/Typography'
import ArrowIcon from 'public/icons/arrows/arrow-right.svg'
import CircleArrowRightIcon from 'public/icons/arrows/arrow-circle-right.svg'
import OpenIcon from 'public/icons/interface/open.svg'
import HybridIcon from 'public/icons/interface/hybrid.svg'
import CopyrightIcon from 'public/icons/interface/copyright.svg'

export function joinTranslatedTitles(items, locale) {
  return Array.isArray(items)
    ? items
        .map((item) => getPreferredTranslation(item, locale)?.title || item?.title)
        .filter(Boolean)
        .join(', ')
    : ''
}

export function getLicenseModelIcon(idName) {
  if (idName === 'open') return OpenIcon
  if (idName === 'hybrid') return HybridIcon
  if (idName === 'copyright') return CopyrightIcon

  return null
}

export function JournalLogoImage({ media, alt, className }) {
  const src = media?.url ? getMediaUrl(media.url, media.version) : null

  if (!src) {
    return null
  }

  return (
    <Image
      src={src}
      alt={alt || ''}
      width={media.width || 160}
      height={media.height || 80}
      className={`h-auto w-auto object-contain ${className || ''}`}
    />
  )
}

export function JournalLicenseModels({
  items,
  locale,
  fallback = '-',
  itemClassName = 'inline-flex items-center gap-4',
  iconClassName = 'size-21',
  separator = ', ',
}) {
  if (!Array.isArray(items) || items.length === 0) {
    return fallback
  }

  return items.map((item, index) => {
    const Icon = getLicenseModelIcon(item.idName)
    const title = getPreferredTranslation(item, locale)?.title

    return title ? (
      <Fragment key={item.uuid || title}>
        <span className={itemClassName}>
          {Icon && <Icon className={iconClassName} />}
          {title}
        </span>
        {index < items.length - 1 ? separator : ''}
      </Fragment>
    ) : null
  })
}

export function JournalLicenseLinks({
  items,
  locale,
  fallback = '-',
  linkClassName,
  itemClassName,
  separator = ', ',
  emptyHref,
}) {
  if (!Array.isArray(items) || items.length === 0) {
    return fallback
  }

  return items.map((item, index) => {
    const title = getPreferredTranslation(item, locale)?.title
    const href = item.link || emptyHref
    const suffix = index < items.length - 1 ? separator : ''

    if (!title) {
      return null
    }

    return href ? (
      <Link key={item.uuid || title} href={href} className={linkClassName}>
        {title}
        {suffix}
      </Link>
    ) : (
      <span key={item.uuid || title} className={itemClassName}>
        {title}
        {suffix}
      </span>
    )
  })
}

// Link w linii tekstu: długa nazwa zawija się razem z podkreśleniem (tło, nie pseudoelement na całą szerokość),
// a przecinek zostaje przy ostatnim słowie.
export const INLINE_LIST_LINK_CLASS_NAME =
  'bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-left-bottom bg-[length:100%_1px] transition-[background-size] hover:bg-[length:0%_1px]'
const SEARCH_FILTER_LINK_CLASS_NAME = INLINE_LIST_LINK_CLASS_NAME

// Dziedzina/dyscyplina prowadzi do wyszukiwarki zaawansowanej z ustawionym filtrem, jak kafle na stronie głównej.
// Wyszukiwarka żyje na portalu głównym - na domenie czasopisma link musi być absolutny.
// Pozycja bez numerycznego `id` (patrz attachSearchIds) zostaje zwykłym tekstem.
export function SearchFilterLinks({ items, name, locale }) {
  const { advancedSearchResultsPath } = useContext(GlobalContext)
  const mainUrl = String(process.env.NEXT_PUBLIC_MAIN_URL || '').replace(/\/$/, '')
  const path =
    advancedSearchResultsPath ||
    (locale === 'en' ? '/en/advanced-search-results' : '/wyniki-wyszukiwania-zaawansowanego')
  const entries = (items || [])
    .map((item) => ({ item, title: getPreferredTranslation(item, locale)?.title || item?.title }))
    .filter(({ title }) => title)

  return entries.map(({ item, title }, index) => (
    <Fragment key={item.uuid || title}>
      {item.id ? (
        <Link
          href={`${mainUrl}${path}?${serializeAdvancedSearchData({ option: 'page', [name]: item.id }, locale).toString()}`}
          className={SEARCH_FILTER_LINK_CLASS_NAME}
        >
          {title}
        </Link>
      ) : (
        title
      )}
      {index < entries.length - 1 ? ', ' : ''}
    </Fragment>
  ))
}

export function JournalInfoItem({ label, children, className = '' }) {
  if (!children) {
    return null
  }

  return (
    <div className={`flex flex-col gap-11 ${className}`.trim()}>
      <Typography variant="h3-bold" component="h3" color="text-basic-black">
        {label}
      </Typography>
      <Typography variant="h3-light" component="div" color="text-basic-black">
        {children}
      </Typography>
    </div>
  )
}

export function JournalInfoDivider() {
  return <hr className="col-span-full h-1 w-full border-0 bg-dark-blue-dark-blue6" />
}

function compactJournalInfoItems(items) {
  const compactedItems = []

  items.forEach((item) => {
    const content = item.content
    const hasContent =
      item.type === 'divider' ||
      (content !== null &&
        content !== undefined &&
        content !== false &&
        (!Array.isArray(content) || content.length > 0) &&
        (typeof content !== 'string' || content.trim() !== ''))

    if (!hasContent) return

    if (item.type === 'divider') {
      const previousItem = compactedItems.at(-1)

      if (!previousItem || previousItem.type === 'divider') return
    }

    compactedItems.push(item)
  })

  if (compactedItems.at(-1)?.type === 'divider') {
    compactedItems.pop()
  }

  return compactedItems
}

export const JournalInfoGrid = forwardRef(function JournalInfoGrid(
  { items, className = 'grid gap-32 d:grid-cols-2' },
  ref,
) {
  const compactedItems = compactJournalInfoItems(items)

  return (
    <div ref={ref} className={className}>
      {compactedItems.map((item, index) => {
        const key = item.key || `${item.type || item.label}-${index}`

        if (item.type === 'divider') {
          return <JournalInfoDivider key={key} />
        }

        if (item.type === 'custom') {
          return <Fragment key={key}>{item.content}</Fragment>
        }

        return (
          <JournalInfoItem key={key} label={item.label} className={item.className}>
            {item.content}
          </JournalInfoItem>
        )
      })}
    </div>
  )
})

export function JournalIssueAside({
  title,
  cover,
  coverLarge,
  coverWidth,
  coverHeight,
  coverLargeWidth,
  coverLargeHeight,
  alt,
  issueTitle,
  issueSubtitle,
  volumeTitle,
  year,
  issueHref,
  issuesHref,
  tableOfContentsHref,
  tableOfContentsLabel,
  tableOfContentsMeta,
  tableOfContentsFullWidth = false,
  tableOfContentsTargetBlank = false,
  showAllIssues = true,
  color = 'black',
  translate,
}) {
  const isPlatformColor = color === 'platform'
  const linkColor = isPlatformColor ? 'text-dark-blue-dark-blue2' : 'text-basic-black'
  const underlineColor = isPlatformColor ? 'after:bg-dark-blue-dark-blue2' : 'after:bg-basic-black'
  const buttonClassName = isPlatformColor
    ? 'w-fit'
    : 'w-fit !border-basic-black !text-basic-black supports-hover:hover:!border-basic-black supports-hover:hover:!bg-basic-gray10 supports-hover:hover:!text-basic-black'

  return (
    <aside className="flex flex-col gap-24">
      {title && (
        <Typography variant="h3-caps-bold" component="h2" color="text-basic-black">
          {title}
        </Typography>
      )}
      {cover && (
        <Lightbox
          imageSrc={coverLarge || cover}
          alt={alt}
          className="flex"
          imageClassName="cover-drop-shadow"
          width={coverLargeWidth || coverWidth || 309}
          height={coverLargeHeight || coverHeight || 426}
        >
          <Image
            src={cover}
            alt={alt}
            width={coverWidth || 309}
            height={coverHeight || 426}
            priority
            fetchPriority="high"
            sizes="(max-width: 768px) 60vw, 309px"
            className="cover-shadow w-full max-w-309 object-contain"
          />
        </Lightbox>
      )}
      {(issueTitle || issueSubtitle) && (
        <div>
          {issueTitle && (
            <Typography variant="body1" component="h3" color="text-basic-black" className="pb-6">
              {issueTitle}
              {volumeTitle ? `, ${volumeTitle}` : ''}
              {year ? `, ${year}` : ''}
            </Typography>
          )}
          {issueSubtitle && (
            <Typography variant="body3" color="text-basic-black" className="pb-6">
              {issueSubtitle}
            </Typography>
          )}
        </div>
      )}
      {issueHref && (
        <Link href={issueHref} className="group mt-6 flex w-fit items-center gap-6">
          <Typography
            variant="button-l-bold"
            component="span"
            color={linkColor}
            className={`relative block w-fit after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:transition-[width] supports-hover:group-hover:after:w-full ${underlineColor}`}
          >
            {translate('T_SEE_THE_NUMBER')}
          </Typography>
          <ArrowIcon className={linkColor} />
        </Link>
      )}
      {issuesHref && <hr className="h-1 w-full border-0 bg-dark-blue-dark-blue6" />}
      {tableOfContentsHref && (
        <div className="flex w-full flex-col gap-6">
          <Button
            href={tableOfContentsHref}
            className={tableOfContentsFullWidth ? 'relative w-full !justify-center' : buttonClassName}
            contentClassName={tableOfContentsFullWidth ? '!static' : ''}
            textClassName={tableOfContentsFullWidth ? '!static' : ''}
            target={tableOfContentsTargetBlank ? '_blank' : undefined}
            rel={tableOfContentsTargetBlank ? 'noreferrer' : undefined}
          >
            <span className={tableOfContentsFullWidth ? 'flex items-center justify-center' : 'flex items-center gap-8'}>
              {tableOfContentsLabel}
              {tableOfContentsFullWidth ? (
                <CircleArrowRightIcon className={`absolute right-16 size-21 shrink-0 ${linkColor}`} />
              ) : (
                <span
                  className={`flex size-18 items-center justify-center rounded-full border ${
                    isPlatformColor ? 'border-dark-blue-dark-blue2' : 'border-basic-black'
                  }`}
                >
                  <ArrowIcon className={linkColor} />
                </span>
              )}
            </span>
          </Button>
          {tableOfContentsMeta && (
            <Typography variant="body4" component="span" color={linkColor}>
              {tableOfContentsMeta}
            </Typography>
          )}
        </div>
      )}
      {showAllIssues && issuesHref && (
        <Button href={issuesHref} className={buttonClassName}>
          <span className="flex items-center gap-8">
            {translate('T_SEE_ALL_NUMBERS')}
            <span
              className={`flex size-18 items-center justify-center rounded-full border ${
                isPlatformColor ? 'border-dark-blue-dark-blue2' : 'border-basic-black'
              }`}
            >
              <ArrowIcon className={linkColor} />
            </span>
          </span>
        </Button>
      )}
    </aside>
  )
}
